import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Button, Snackbar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import { useAuth } from './AuthContext';
import BotaoArquivar from './BotaoArquivar';
import { API_URL } from './config';

// ════════════════════════════════════════════════════════════
// CSS DO FORMULÁRIO PNCD — injetado uma vez no DOM
// ════════════════════════════════════════════════════════════

const PNCD_CSS = `
:root {
  --fad07-bd: #000;
  --fad07-bdw: 0.5pt;
  --fad07-gray: #e7e7e7;
  --fad07-gray2: #dcdcdc;
  --fad07-blue: #1d3a8c;
  --fz-num: 6.5pt;
  --fz-label: 7pt;
  --fz-val: 9pt;
}

/* ═══ TELA ═══ */
.fad07-pncd-shell {
  display: flex;
  justify-content: center;
  padding: 18px 0;
  background: #efeee9;
  min-height: 100vh;
}
.fad07-page {
  width: 210mm;
  min-height: 297mm;
  padding: 5mm 7mm 5mm 7mm;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: var(--fz-label);
  line-height: 1.15;
  box-shadow: 0 2px 12px rgba(0,0,0,.18);
  position: relative;
}
.fad07-page * { box-sizing: border-box; }

/* ═══ Cabeçalho ═══ */
.fad07-header { position: relative; height: 16mm; margin-bottom: 1.5mm; }
.fad07-logo-slot { position: absolute; left: 0; top: 0; width: 30mm; height: 16mm; }
.fad07-titles {
  position: absolute; left: 32mm; right: 42mm; bottom: 0;
  text-align: center; pointer-events: none;
}
.fad07-title-main { font-weight: 600; font-size: 10pt; letter-spacing: .01em; white-space: nowrap; }
.fad07-title-sub {
  font-family: "Arial Black", "Helvetica Neue", Arial, sans-serif;
  font-weight: 900; font-size: 13pt; margin-top: 0.4mm; white-space: nowrap;
}
.fad07-field-01 {
  position: absolute; right: 0; top: 1mm; width: 40mm;
  border: var(--fad07-bdw) solid var(--fad07-bd);
  display: flex; flex-direction: column;
  height: 13mm; background: #fff; z-index: 2;
}
.fad07-field-01 .fad07-h {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  padding: 0.5mm 1mm;
}
.fad07-field-01 .fad07-v { flex: 1; padding: 1mm; min-height: 7mm; }

/* ═══ Células genéricas ═══ */
.fad07-row { display: flex; }
.fad07-row.top .fad07-cell { border-top: var(--fad07-bdw) solid var(--fad07-bd); }
.fad07-cell {
  flex: 1; display: flex; flex-direction: column;
  border-left: var(--fad07-bdw) solid var(--fad07-bd);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  min-width: 0;
}
.fad07-cell .fad07-h {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  padding: 0.5mm 1mm;
  display: flex; align-items: flex-start; gap: 1mm;
  min-height: 5mm;
}
.fad07-cell .fad07-v { flex: 1; padding: 1mm; min-height: 6mm; }
.fad07-num { font-size: var(--fz-num); font-weight: 700; min-width: 5mm; }
.fad07-name { font-size: var(--fz-label); font-weight: 600; line-height: 1.1; }

/* Proporções das linhas de identificação */
.fad07-ident-row-1 .fad07-cell:nth-child(1) { flex: 2; }
.fad07-ident-row-1 .fad07-cell:nth-child(2) { flex: 3; }
.fad07-ident-row-1 .fad07-cell:nth-child(3) { flex: 2; }

.fad07-ident-row-2 .fad07-cell:nth-child(1) { flex: 1.4; }
.fad07-ident-row-2 .fad07-cell:nth-child(2) { flex: 1.8; }
.fad07-ident-row-2 .fad07-cell:nth-child(3) { flex: 1.4; }
.fad07-ident-row-2 .fad07-cell:nth-child(4) { flex: 1.5; }
.fad07-ident-row-2 .fad07-cell:nth-child(5) { flex: 1.5; }
.fad07-ident-row-2 .fad07-cell:nth-child(6) { flex: 1.6; }
.fad07-ident-row-2 .fad07-cell:nth-child(7) { flex: 1.0; }

.fad07-body-split { display: flex; flex: 1; }
.fad07-body-split.split-mini .fad07-v { flex: 0 0 7mm; border-right: var(--fad07-bdw) solid var(--fad07-bd); }
.fad07-body-split.split-mini .fad07-mini-opts {
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  padding: 0.5mm 1mm; gap: 0.4mm;
}
.fad07-mini-opts span { font-size: 6.2pt; line-height: 1.1; }

.fad07-body-split.split-act .fad07-v {
  flex: 0 0 8mm; border-right: var(--fad07-bdw) solid var(--fad07-bd); padding: 1mm;
}
.fad07-activity-opts {
  flex: 1;
  display: grid; grid-template-columns: 1fr 1fr 1fr;
  padding: 0.6mm 1.5mm; gap: 0.4mm 4mm;
}
.fad07-activity-opts span { font-size: 6.3pt; line-height: 1.15; }

.fad07-date {
  flex: 1; display: flex; align-items: center; padding: 0.5mm 0.5mm;
  gap: 0.3mm; font-size: var(--fz-val); min-height: 6mm; justify-content: center;
}
.fad07-date > div:first-child,
.fad07-date > div:nth-child(3) { flex: 1; text-align: center; }
.fad07-date > div:nth-child(2) { color: #666; }
.fad07-date.cycle { gap: 0.5mm; }

/* ═══ Valores preenchidos (azul caneta) ═══ */
.fad07-val-filled {
  color: var(--fad07-blue) !important;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10pt;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.fad07-val-na { color: #000; font-size: 12pt; font-weight: 700; text-align: center; }

/* Input inline para edição direta */
.fad07-input-inline {
  width: 100%;
  border: none; outline: none; background: transparent;
  color: var(--fad07-blue);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10pt;
  line-height: 1.1; padding: 1px 2px; font-weight: 700;
  letter-spacing: 0.01em;
}
.fad07-input-inline:focus { background: #fffbe0; }
.fad07-input-inline.center { text-align: center; }

/* ═══ Botão flutuante ═══ */
.fad07-fab-bar {
  position: fixed; top: 80px; right: 20px; z-index: 1200;
  display: flex; flex-direction: column; gap: 8px;
}

/* ═══ Impressão ═══ */

/* ═══ Banners de seção ═══ */
.fad07-banner {
  background: var(--fad07-gray2);
  border: var(--fad07-bdw) solid var(--fad07-bd);
  text-align: center; font-weight: 700; font-size: 9pt;
  padding: 0.8mm; letter-spacing: 0.04em;
}
.fad07-banner.gap-top { margin-top: 1.5mm; }
.fad07-subbanner {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  border-left: var(--fad07-bdw) solid var(--fad07-bd);
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  font-size: 6.5pt; font-weight: 700; text-align: center; padding: 0.3mm 1mm;
}
.fad07-group { display: flex; flex-direction: column; flex: 1; }
.fad07-group-cols { display: flex; }
.fad07-group-cols .fad07-cell { border-top: 0; }
.cols-6 .fad07-cell, .cols-3 .fad07-cell, .cols-8 .fad07-cell { flex: 1; }
.fad07-flex-row { display: flex; gap: 1mm; margin-top: 0; }
.fad07-flex-box { display: flex; flex-direction: column; }
.fad07-flex-box.noborder { border: none; }
.fad07-row-1326 { display: flex; }
.fad07-row-1326 .fad07-cell { border-top: 0; min-width: 0; }
/* Campo 13 — largura fixa garantida */
.fad07-row-1326 > .fad07-cell:first-child {
  border-top: var(--fad07-bdw) solid var(--fad07-bd);
  flex: 0 0 26mm;
  min-width: 26mm;
}
/* Grupo Pendência (24,25,26) — alargar pra caber "Recuperados" */
.fad07-row-1326 > .fad07-group:last-child {
  flex: 0 0 36mm;
  min-width: 36mm;
}
.fad07-row-1326 > .fad07-group { min-width: 0; }
/* Cabeçalho com altura suficiente */
.fad07-row-1326 .fad07-cell .fad07-h {
  min-height: 10mm;
  padding: 0.8mm 0.8mm 0.5mm 0.8mm;
  align-items: flex-start;
  gap: 0.5mm;
}
/* Labels compactos mas legíveis */
.fad07-row-1326 .fad07-cell .fad07-name {
  font-size: 6.5pt;
  line-height: 1.1;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  flex: 1;
  display: block;
}
.fad07-row-1326 .fad07-cell .fad07-name.tight {
  font-size: 5.8pt;
  line-height: 1.1;
}
/* Valor centralizado */
.fad07-row-1326 .fad07-cell .fad07-v {
  min-height: 9mm;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11pt;
  font-weight: 700;
  padding: 1mm;
}
.fad07-row-1326 .fad07-subbanner {
  min-height: 3.5mm;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 6.2pt;
  padding: 0.4mm 1mm;
}


/* Larvicida e Adulticida */
.fad07-larv {
  border: var(--fad07-bdw) solid var(--fad07-bd);
  display: grid;
  grid-template-columns: 8mm 1fr 1fr 1fr;
  font-size: var(--fz-label);
}
.fad07-larv.cols-adult { grid-template-columns: 1fr 1fr; }
.fad07-larv-title {
  grid-column: 1 / -1;
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  font-weight: 700; font-size: 7pt;
  padding: 0.5mm 1mm; text-align: center; line-height: 1.1;
}
.fad07-larv-rowlbl {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  font-weight: 700; font-size: 7pt; text-align: center; padding: 0.5mm;
}
.fad07-larv-hc {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  padding: 0.5mm 1mm; display: flex; align-items: center; gap: 1mm;
}
.fad07-larv-hc:last-child { border-right: 0; }
.fad07-larv-cell {
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  padding: 1mm; min-height: 6mm; text-align: center;
}
.fad07-larv-cell:last-child { border-right: 0; }
.fad07-attention { padding: 1mm 2mm; font-size: 6.5pt; line-height: 1.3; }
.fad07-att-title { font-weight: 700; margin-bottom: 0.8mm; }
.fad07-attention > div { margin-bottom: 0.3mm; }

/* ═══ Laboratório — Matrizes ═══ */
.fad07-lab-mat { display: grid; margin-top: 0; }
.fad07-lab-mat-a { grid-template-columns: 32mm repeat(8, 1fr); }
.fad07-lab-mat-imv { grid-template-columns: 32mm repeat(6, 1fr); }
.fad07-lab-mat-exm { grid-template-columns: repeat(4, 1fr); }
.fad07-lab-title {
  grid-column: 1 / -1;
  background: var(--fad07-gray2);
  border: var(--fad07-bdw) solid var(--fad07-bd);
  text-align: center; font-weight: 700; font-size: 8pt; padding: 0.5mm;
}
.fad07-lab-title-spanning {
  grid-column: 1 / -1;
  background: var(--fad07-gray2);
  border: var(--fad07-bdw) solid var(--fad07-bd);
  text-align: center; font-weight: 700; font-size: 8pt; padding: 0.5mm;
}
.fad07-lab-corner {
  background: var(--fad07-gray);
  border: var(--fad07-bdw) solid var(--fad07-bd); border-top: 0;
}
.fad07-lab-colhdr {
  background: var(--fad07-gray);
  border: var(--fad07-bdw) solid var(--fad07-bd);
  border-top: 0; border-left: 0;
  padding: 0.5mm 1mm; text-align: center; font-size: 6.5pt;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  line-height: 1.1;
}
.fad07-lab-rowlbl {
  background: var(--fad07-gray);
  border: var(--fad07-bdw) solid var(--fad07-bd);
  border-top: 0;
  padding: 0.5mm 1mm; display: flex; align-items: center;
  font-weight: 600; font-size: 7pt;
}
.fad07-lab-rowlbl em { font-style: italic; }
.fad07-lab-cell {
  border: var(--fad07-bdw) solid var(--fad07-bd);
  border-top: 0; border-left: 0;
  padding: 1mm; min-height: 6mm; text-align: center;
}

/* ═══ Quarteirões 63/64/65 ═══ */
.fad07-quart {
  flex: 1; border: var(--fad07-bdw) solid var(--fad07-bd);
  display: flex; flex-direction: column;
}
.fad07-quart-title {
  background: var(--fad07-gray);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  padding: 0.5mm 1mm; display: flex; gap: 1mm; align-items: flex-start;
  font-size: 6.5pt; min-height: 7mm;
}
.fad07-quart-title em { font-style: italic; }
.fad07-quart-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(7, 1fr);
  flex: 1;
}
.fad07-quart-cell {
  border-right: var(--fad07-bdw) solid var(--fad07-bd);
  border-bottom: var(--fad07-bdw) solid var(--fad07-bd);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5mm;
  padding: 0.3mm;
  min-height: 4mm;
  font-size: 7pt;
}
.fad07-quart-cell input.fad07-q-num {
  width: 45%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--fad07-blue);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 8pt;
  font-weight: 700;
  text-align: center;
  padding: 0;
}
.fad07-quart-cell input.fad07-q-num:focus { background: #fffbe0; }
.fad07-quart-cell .fad07-q-sep { color: #999; font-weight: 400; }

/* ═══ Rodapé ═══ */
.fad07-footer-row { margin-top: 1.5mm; }
.fad07-footer-row .fad07-cell { flex: 1; }
.fad07-footer-row .fad07-v.signature { min-height: 12mm; }
.fad07-footer-mark { padding-left: 0.5mm; }
.fad07-tovis-footer {
  margin-top: 1mm; padding-top: 0.8mm;
  border-top: 0.3pt solid #aaa;
  text-align: center; font-size: 6pt; color: #777; font-style: italic;
}


/* ═══ DatePicker nativo nos campos 08/09 ═══ */
.fad07-date-native {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--fad07-blue);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9pt;
  font-weight: 700;
  text-align: center;
  padding: 1mm 2mm;
  letter-spacing: 0.01em;
  cursor: pointer;
}
.fad07-date-native:focus { background: #fffbe0; }
.fad07-date-native::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: invert(0.4);
  margin-left: 1mm;
}

/* ═══ Select S/N do campo 10 ═══ */
.fad07-select-sn {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--fad07-blue);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10pt;
  font-weight: 700;
  text-align: center;
  text-align-last: center;
  padding: 1mm 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
.fad07-select-sn:focus { background: #fffbe0; }

@media print {
  @page { size: A4 portrait; margin: 0; }
  html, body { background: #fff !important; }
  body * { visibility: hidden !important; }
  .fad07-pncd-shell, .fad07-pncd-shell * { visibility: visible !important; }
  .fad07-pncd-shell { padding: 0 !important; background: #fff !important; }
  .fad07-page { box-shadow: none !important; }
  .fad07-fab-bar, .fad07-selectors-bar { display: none !important; }
  .fad07-input-inline { color: var(--fad07-blue) !important; }
}
`;

// Estilo do FAD-07 PE: entra quando a tela do PE abre e sai quando ela fecha (Arquivos, 01/10/2026).
// Antes ficava ligado o tempo todo e, na impressão, escondia as outras telas (impressão em branco
// no FAD-07, Auditoria do Fechamento, Quadro LIRAa, Quadro do Supervisor e Fechamento de Ciclo).
function instalarEstiloPncd() {
  if (typeof document === 'undefined') return;
  document.getElementById('fad07-pncd-style')?.remove();
  const styleEl = document.createElement('style');
  styleEl.id = 'fad07-pncd-style';
  styleEl.appendChild(document.createTextNode(PNCD_CSS));
  document.head.appendChild(styleEl);
}
function removerEstiloPncd() {
  if (typeof document === 'undefined') return;
  document.getElementById('fad07-pncd-style')?.remove();
}

// ════════════════════════════════════════════════════════════
// TIPOS
// ════════════════════════════════════════════════════════════

interface CicloPE {
  id: number;
  numero: number;
  ano: number;
  data_inicio_referencia: string | null;
  data_fim_referencia: string | null;
  ativo: boolean;
  encerrado: boolean;
}

interface Area {
  id: number;
  nome: string;
  zona?: string;
}

interface SemanaDisponivel {
  ano: number;
  se: number;
}

interface RelatorioCompleto {
  municipio: string;
  area_id: number;
  area_nome: string;
  area_zona: string;
  ciclo_pe_id: number;
  ciclo_numero: number;
  ciclo_ano: number;
  ciclo_data_inicio: string | null;
  ciclo_data_fim: string | null;
  ciclo_encerrado: boolean;
  semana_epi: number;
  ano: number;
  [key: string]: any;
}

interface Props {
  onVoltar: () => void;
}

// ════════════════════════════════════════════════════════════
// COMPONENTE
// ════════════════════════════════════════════════════════════

export default function FAD07PE({ onVoltar }: Props) {
  useLayoutEffect(() => { instalarEstiloPncd(); return removerEstiloPncd; }, []);
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Seletores
  const [ciclos, setCiclos] = useState<CicloPE[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [semanas, setSemanas] = useState<SemanaDisponivel[]>([]);
  const [cicloId, setCicloId] = useState<number | ''>('');
  const [areaId, setAreaId] = useState<number | ''>('');
  const [semanaEpi, setSemanaEpi] = useState<number | ''>('');
  const [ano, setAno] = useState<number>(new Date().getFullYear());

  // Dados
  const [dados, setDados] = useState<RelatorioCompleto | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Arquivos: dados do documento para o botão "Arquivar" (sem ciclo_id: o ciclo do PE é de outra tabela)
  // Bloco 8: cada SEMANA é um documento (SE e ano = campo 11 "Sem. Epidem." da folha)
  const pedidoArquivo = useMemo(() => {
    if (!dados || !cicloId || !areaId || !dados.semana_epi || !dados.ano) return null;
    const se = Number(dados.semana_epi);
    const anoSe = Number(dados.ano);
    const c = ciclos.find(x => x.id === cicloId);
    const nomeCiclo = c ? `Ciclo PE ${c.numero}/${c.ano}` : `Ciclo PE ${dados.ciclo_numero}/${dados.ciclo_ano}`;
    const nomeArea = areas.find(a => a.id === areaId)?.nome ?? dados.area_nome ?? `Área ${areaId}`;
    return {
      pasta: 'fad07_pe' as const, tipo: 'fad07_pe',
      chave: `fad07_pe|cpe${cicloId}|a${areaId}|se${se}|${anoSe}`,
      titulo: `FAD-07 PE · ${nomeArea} · SE ${se} · ${nomeCiclo}`,
      area_id: Number(areaId), se, ano: anoSe,
      referencia: `SE ${se}/${anoSe}`, avisos: [] as string[],
    };
  }, [dados, cicloId, areaId, ciclos, areas]);

  // Campos manuais que não vêm do banco
  const [campo01Controle, setCampo01Controle] = useState('');
  const [campo05Categ, setCampo05Categ] = useState('BRR');
  const [campo06Tipo, setCampo06Tipo] = useState('1');
  const [campo10Concluido, setCampo10Concluido] = useState('S');
  const [data08, setData08] = useState({ d: '', m: '', y: '' });
  const [data09, setData09] = useState({ d: '', m: '', y: '' });

  // Snackbar
  const [snackMsg, setSnackMsg] = useState<string | null>(null);

  // ── Carregar catálogos ao montar
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [rc, ra] = await Promise.all([
          axios.get<CicloPE[]>(`${API_URL}/api/ciclos-pe/`, auth),
          axios.get<Area[]>(`${API_URL}/api/areas/`, auth),
        ]);
        setCiclos(rc.data || []);
        setAreas(ra.data || []);
        const ativo = rc.data?.find((c) => c.ativo);
        if (ativo) setCicloId(ativo.id);
      } catch (e: any) {
        setErro('Falha ao carregar catálogos: ' + (e?.message || 'erro'));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Carregar semanas ao mudar ciclo+área
  useEffect(() => {
    if (!cicloId || !areaId) { setSemanas([]); return; }
    (async () => {
      try {
        const r = await axios.get<SemanaDisponivel[]>(
          `${API_URL}/api/fad07-pe/semanas-disponiveis?ciclo_pe_id=${cicloId}&area_id=${areaId}`,
          auth,
        );
        setSemanas(r.data || []);
      } catch {
        setSemanas([]);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloId, areaId]);

  // ── Carregar dados completos
  const carregarDados = useCallback(async () => {
    if (!cicloId || !areaId || !semanaEpi) { setDados(null); return; }
    setCarregando(true);
    setErro(null);
    try {
      const r = await axios.get<RelatorioCompleto>(
        `${API_URL}/api/fad07-pe/relatorio-completo?ciclo_pe_id=${cicloId}&area_id=${areaId}&semana_epi=${semanaEpi}&ano=${ano}`,
        auth,
      );
      setDados(r.data);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setErro(typeof detail === 'string' ? detail : 'Falha ao carregar dados.');
      setDados(null);
    } finally {
      setCarregando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloId, areaId, semanaEpi, ano]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // ── Estado manuais (sincronizado com dados do servidor)
  const [manuais, setManuais] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!dados) return;
    if (dados.ciclo_data_inicio) {
      const [y, m, d] = dados.ciclo_data_inicio.split('-');
      setData08({ d, m, y });
    }
    if (dados.ciclo_data_fim) {
      const [y, m, d] = dados.ciclo_data_fim.split('-');
      setData09({ d, m, y });
    }
    const camposManuais = [
      'agentes_seg','agentes_ter','agentes_qua','agentes_qui','agentes_sex',
      'total_dia_seg','total_dia_ter','total_dia_qua','total_dia_qui','total_dia_sex',
      'campo_43_total_agentes','campo_44_total_dias',
      'campo_45_aeg_a1','campo_46_aeg_a2','campo_47_aeg_b','campo_48_aeg_c',
      'campo_49_aeg_d1','campo_50_aeg_d2','campo_51_aeg_e','campo_52_aeg_total','campo_52_alb_total',
      'campo_53_aeg_residencia','campo_54_aeg_comercio','campo_55_aeg_tb',
      'campo_56_aeg_pe','campo_57_aeg_outros','campo_58_aeg_total',
      'campo_53_alb_residencia','campo_54_alb_comercio','campo_55_alb_tb',
      'campo_56_alb_pe','campo_57_alb_outros','campo_58_alb_total',
      'campo_53_out_residencia','campo_54_out_comercio','campo_55_out_tb',
      'campo_56_out_pe','campo_57_out_outros','campo_58_out_total',
      'campo_59_aeg_larvas','campo_60_aeg_pupas','campo_61_aeg_exuvias','campo_62_aeg_adultos',
      'campo_59_alb_larvas','campo_60_alb_pupas','campo_61_alb_exuvias','campo_62_alb_adultos',
      'campo_59_out_larvas','campo_60_out_pupas','campo_61_out_exuvias','campo_62_out_adultos',
      'campo_63_quart_aegypti','campo_64_quart_albopictus','campo_65_quart_ambos',
    ];
    const m: Record<string, any> = {};
    for (const k of camposManuais) m[k] = dados[k] ?? '';
    if (!m['campo_43_total_agentes'] || m['campo_43_total_agentes'] === 0) m['campo_43_total_agentes'] = 1;
    if (!m['campo_44_total_dias'] || m['campo_44_total_dias'] === 0) m['campo_44_total_dias'] = 1;
    setManuais(m);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  const editar = (chave: string, valor: any) =>
    setManuais((prev) => ({ ...prev, [chave]: valor }));
  const editarNum = (chave: string, valor: string) =>
    setManuais((prev) => ({ ...prev, [chave]: Number(valor) || 0 }));
  const get = (chave: string) => manuais[chave] ?? '';

  // ── Salvar real
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    if (!cicloId || !areaId || !semanaEpi) {
      setSnackMsg('Selecione Ciclo + Área + Semana antes de salvar.');
      return;
    }
    setSalvando(true);
    try {
      await axios.post(`${API_URL}/api/fad07-pe/salvar-tudo`, {
        ciclo_pe_id: cicloId, area_id: areaId, semana_epi: semanaEpi, ano,
        ...manuais,
      }, auth);
      setSnackMsg('✅ Salvo com sucesso!');
      await carregarDados();
    } catch (e: any) {
      const det = e?.response?.data?.detail;
      const msg = Array.isArray(det) ? det.map((x: any) => x.msg).join('; ') : (det || e?.message);
      setSnackMsg('❌ Erro ao salvar: ' + msg);
    } finally {
      setSalvando(false);
    }
  };

  // ── Helpers
  const dataPartes = (s: string | null | undefined) => {
    if (!s) return { d: '', m: '', y: '' };
    const [y, m, d] = s.split('-');
    return { d, m, y };
  };

  const CelValor = ({ valor, naoSeAplica }: { valor?: any; naoSeAplica?: boolean }) => {
    if (naoSeAplica) return <span className="fad07-val-na">-</span>;
    if (valor === null || valor === undefined || valor === '') return <span>&nbsp;</span>;
    return <span className="fad07-val-filled">{String(valor)}</span>;
  };

  const InputInline = ({ value, onChange, center }: {
    value: string; onChange: (v: string) => void; center?: boolean;
  }) => (
    <input
      className={`fad07-input-inline${center ? ' center' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );

  // ── Inputs inline numérico e texto (azul Arial bold)
  const InputNum = ({ chave }: { chave: string }) => {
    const v = manuais[chave];
    const display = (v === null || v === undefined || v === '' || v === 0) ? '' : String(v);
    return (
      <input
        className="fad07-input-inline center"
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) => editarNum(chave, e.target.value)}
      />
    );
  };
  const InputText = ({ chave, placeholder }: { chave: string; placeholder?: string }) => (
    <input
      className="fad07-input-inline"
      type="text"
      value={String(get(chave))}
      placeholder={placeholder}
      onChange={(e) => editar(chave, e.target.value)}
    />
  );

  // DatePicker nativo (input type="date") — abre calendário do navegador
  const InputData = ({
    valor, onChange,
  }: {
    valor: { d: string; m: string; y: string };
    onChange: (v: { d: string; m: string; y: string }) => void;
  }) => {
    const isoVal =
      valor.y && valor.m && valor.d
        ? `${valor.y.padStart(4, '0')}-${valor.m.padStart(2, '0')}-${valor.d.padStart(2, '0')}`
        : '';

    const handleChange = (iso: string) => {
      if (!iso) {
        onChange({ d: '', m: '', y: '' });
        return;
      }
      const [y, m, d] = iso.split('-');
      onChange({ d, m, y });
    };

    return (
      <input
        type="date"
        className="fad07-date-native"
        value={isoVal}
        onChange={(e) => handleChange(e.target.value)}
      />
    );
  };

  const QuarteiraoGrid = ({ chave }: { chave: string }) => {
    const texto = String(get(chave) || '');
    const pares = texto
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0)
      .map((s: string) => {
        const [a, b] = s.split('/');
        return [a?.trim() || '', b?.trim() || ''];
      });
    while (pares.length < 28) pares.push(['', '']);

    const editarPar = (idx: number, lado: 0 | 1, valor: string) => {
      const novos = [...pares];
      novos[idx] = [...novos[idx]] as [string, string];
      novos[idx][lado] = valor.replace(/\D/g, '');
      const texto_novo = novos
        .filter(([a, b]) => a || b)
        .map(([a, b]) => `${a || ''}/${b || ''}`)
        .join(', ');
      editar(chave, texto_novo);
    };

    return (
      <div className="fad07-quart-grid">
        {pares.slice(0, 28).map((par, idx) => (
          <div className="fad07-quart-cell" key={idx}>
            <input
              className="fad07-q-num"
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={par[0]}
              onChange={(e) => editarPar(idx, 0, e.target.value)}
            />
            <span className="fad07-q-sep">/</span>
            <input
              className="fad07-q-num"
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={par[1]}
              onChange={(e) => editarPar(idx, 1, e.target.value)}
            />
          </div>
        ))}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════

  return (
    <Box>
      {/* ─── Barra seletores (não imprime) ─── */}
      <Box
        className="fad07-selectors-bar"
        sx={{ p: 2, bgcolor: 'background.paper', borderBottom: '1px solid #e0e0e0' }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
          <Button variant="text" size="small" onClick={onVoltar} sx={{ mr: 1 }}>
            ← Voltar
          </Button>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Ciclo PE</InputLabel>
            <Select
              label="Ciclo PE"
              value={cicloId}
              onChange={(e) => setCicloId(Number(e.target.value))}
            >
              {ciclos.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  Ciclo {c.numero}/{c.ano}{c.ativo ? ' ●' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Área</InputLabel>
            <Select
              label="Área"
              value={areaId}
              onChange={(e) => setAreaId(Number(e.target.value))}
            >
              {areas.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Semana Epi</InputLabel>
            <Select
              label="Semana Epi"
              value={semanaEpi}
              onChange={(e) => { setSemanaEpi(Number(e.target.value)); }}
            >
              {semanas.length > 0
                ? semanas.map((s) => (
                    <MenuItem key={`${s.ano}-${s.se}`} value={s.se}>
                      SE {s.se}/{s.ano}
                    </MenuItem>
                  ))
                : <MenuItem value={20}>SE 20/{ano} (teste)</MenuItem>
              }
            </Select>
          </FormControl>

          {carregando && <CircularProgress size={20} />}
        </Stack>

        {erro && <Alert severity="error" sx={{ mt: 1 }}>{erro}</Alert>}
      </Box>

      {/* ─── Botões flutuantes (não imprimem) ─── */}
      {dados && (
        <Box className="fad07-fab-bar">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={salvar}
            disabled={salvando}
            sx={{ boxShadow: 3 }}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ bgcolor: 'white', boxShadow: 2 }}
          >
            Imprimir
          </Button>
          <BotaoArquivar alvo={() => document.querySelector('.fad07-pncd-shell')} pedido={pedidoArquivo} />
        </Box>
      )}

      {/* ─── Formulário PNCD ─── */}
      <Box className="fad07-pncd-shell">
        <Box className="fad07-page">

          {/* ═══ Cabeçalho + Campo 01 ═══ */}
          <div className="fad07-header">
            <div className="fad07-logo-slot" />
            <div className="fad07-titles">
              <div className="fad07-title-main">PROGRAMA NACIONAL DE CONTROLE DA DENGUE — PNCD</div>
              <div className="fad07-title-sub">RESUMO SEMANAL DO SERVIÇO ANTIVETORIAL</div>
            </div>
            <div className="fad07-field-01">
              <div className="fad07-h">
                <div className="fad07-num">01</div>
                <div className="fad07-name">Controle Digitação</div>
              </div>
              <div className="fad07-v">
                <InputInline value={campo01Controle} onChange={setCampo01Controle} />
              </div>
            </div>
          </div>

          {/* ═══ Linha 1: 02 Município | 03 Localidade | 04 Zona ═══ */}
          <div className="fad07-row top fad07-ident-row-1">
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">02</div><div className="fad07-name">Município</div></div>
              <div className="fad07-v"><CelValor valor={dados?.municipio} /></div>
            </div>
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">03</div><div className="fad07-name">Código e nome da localidade</div></div>
              <div className="fad07-v"><CelValor valor={dados?.area_nome} /></div>
            </div>
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">04</div><div className="fad07-name">Zona (Agente)</div></div>
              <div className="fad07-v"><CelValor valor={dados?.area_zona} /></div>
            </div>
          </div>

          {/* ═══ Linha 2: 05-11 ═══ */}
          <div className="fad07-row fad07-ident-row-2">
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">05</div><div className="fad07-name">Categ. Localid.</div></div>
              <div className="fad07-v">
                <InputInline value={campo05Categ} onChange={setCampo05Categ} center />
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">06</div><div className="fad07-name">Tipo</div></div>
              <div className="fad07-body-split split-mini">
                <div className="fad07-v">
                  <InputInline value={campo06Tipo} onChange={setCampo06Tipo} center />
                </div>
                <div className="fad07-mini-opts">
                  <span>1 - sede</span>
                  <span>2 - outros</span>
                </div>
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">07</div><div className="fad07-name">Ciclo/Ano</div></div>
              <div className="fad07-date cycle">
                <div><CelValor valor={dados?.ciclo_numero} /></div>
                <div>/</div>
                <div><CelValor valor={dados?.ciclo_ano} /></div>
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">08</div><div className="fad07-name">Data início</div></div>
              <div className="fad07-date">
                <InputData valor={data08} onChange={setData08} />
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">09</div><div className="fad07-name">Data final</div></div>
              <div className="fad07-date">
                <InputData valor={data09} onChange={setData09} />
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">10</div><div className="fad07-name">Concluído?</div></div>
              <div className="fad07-body-split split-mini">
                <div className="fad07-v" style={{padding:0, display:'flex', alignItems:'center', justifyContent:'center'}}>
                  <select
                    className="fad07-select-sn"
                    value={campo10Concluido}
                    onChange={(e) => setCampo10Concluido(e.target.value)}
                  >
                    <option value="S">S</option>
                    <option value="N">N</option>
                  </select>
                </div>
                <div className="fad07-mini-opts">
                  <span>S - Sim</span>
                  <span>N - Não</span>
                </div>
              </div>
            </div>

            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">11</div><div className="fad07-name">Sem. Epidem.</div></div>
              <div className="fad07-v" style={{textAlign:'center'}}>
                <CelValor valor={dados ? `${dados.semana_epi}/${dados.ano}` : null} />
              </div>
            </div>
          </div>

          {/* ═══ Linha 3: 12 Atividade ═══ */}
          <div className="fad07-row">
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">12</div><div className="fad07-name">Atividade</div></div>
              <div className="fad07-body-split split-act">
                <div className="fad07-v"><span className="fad07-val-filled">3</span></div>
                <div className="fad07-activity-opts">
                  <span>1 - LI — Levantamento de Índice</span>
                  <span>2 - LI+T — Levantamento de Índice + Tratamento</span>
                  <span>3 - PE — Ponto Estratégico</span>
                  <span>4 - T — Tratamento</span>
                  <span>5 - DF — Delimitação de Foco</span>
                  <span>6 - PVE — Pesquisa Vetorial Especial</span>
                </div>
              </div>
            </div>
          </div>

          {/* ════ BLOCO 2 — RESUMO DO TRABALHO DE CAMPO (campos 13-44) ════ */}
          <div className="fad07-banner">Resumo do Trabalho de Campo</div>

          {/* Linha 13..26 */}
          <div className="fad07-row-1326">
            <div className="fad07-cell" style={{flex:1}}>
              <div className="fad07-h"><div className="fad07-num">13</div><div className="fad07-name tight">Quart. Concluídos</div></div>
              <div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_13_quart_concl} /></div>
            </div>
            <div className="fad07-group" style={{flex:6}}>
              <div className="fad07-subbanner">Nº imóveis trabalhados por tipo</div>
              <div className="fad07-group-cols cols-6">
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">14</div><div className="fad07-name tight">Residência</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor naoSeAplica /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">15</div><div className="fad07-name tight">Comércio</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor naoSeAplica /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">16</div><div className="fad07-name tight">TB</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor naoSeAplica /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">17</div><div className="fad07-name tight">PE</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_17_pe} /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">18</div><div className="fad07-name tight">Outro</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor naoSeAplica /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">19</div><div className="fad07-name tight">Total</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_19_total} /></div></div>
              </div>
            </div>
            <div className="fad07-group" style={{flex:3}}>
              <div className="fad07-subbanner">Nº imóveis</div>
              <div className="fad07-group-cols cols-3">
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">20</div><div className="fad07-name tight">Trat. Focal</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_20_trat_focal} /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">21</div><div className="fad07-name tight">Trat. Perif.</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor naoSeAplica /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">22</div><div className="fad07-name tight">Inspecion.</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_22_inspecionados} /></div></div>
              </div>
            </div>
            <div className="fad07-cell" style={{flex:1}}>
              <div className="fad07-h"><div className="fad07-num">23</div><div className="fad07-name tight">Amostras Coletadas</div></div>
              <div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_23_amostras} /></div>
            </div>
            <div className="fad07-group" style={{flex:3}}>
              <div className="fad07-subbanner">Pendência</div>
              <div className="fad07-group-cols cols-3">
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">24</div><div className="fad07-name tight">Recusa</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_24_recusa} /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">25</div><div className="fad07-name tight">Fecha-<br/>dos</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_25_fechados} /></div></div>
                <div className="fad07-cell"><div className="fad07-h"><div className="fad07-num">26</div><div className="fad07-name tight">Recuperados</div></div><div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_26_recuperados} /></div></div>
              </div>
            </div>
          </div>

          {/* Linha 27..38 — depósitos + larvicida */}
          <div className="fad07-flex-row">
            <div className="fad07-flex-box" style={{flex:9}}>
              <div className="fad07-group">
                <div className="fad07-subbanner">Nº depósitos inspecionados por tipo</div>
                <div className="fad07-group-cols cols-8">
                  {([['27','campo_27_a1','A1'],['28','campo_28_a2','A2'],['29','campo_29_b','B'],['30','campo_30_c','C'],['31','campo_31_d1','D1'],['32','campo_32_d2','D2'],['33','campo_33_e','E'],['34','campo_34_total_dep','Total']] as const).map(([n,k,lbl])=>(
                    <div key={n} className="fad07-cell">
                      <div className="fad07-h"><div className="fad07-num">{n}</div><div className="fad07-name tight">{lbl}</div></div>
                      <div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.[k]} /></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="fad07-cell">
                <div className="fad07-h"><div className="fad07-num">35</div><div className="fad07-name tight">Depósito Eliminado</div></div>
                <div className="fad07-v" style={{textAlign:'center'}}><CelValor valor={dados?.campo_35_eliminado} /></div>
              </div>
            </div>
            <div className="fad07-flex-box" style={{flex:4}}>
              <div className="fad07-larv">
                <div className="fad07-larv-title">Nº dep. tratados / Larvicida</div>
                <div className="fad07-larv-rowlbl">&nbsp;</div>
                <div className="fad07-larv-hc"><div className="fad07-num">36</div><div className="fad07-name">Tipo</div></div>
                <div className="fad07-larv-hc"><div className="fad07-num">37</div><div className="fad07-name">Qtde. (g)</div></div>
                <div className="fad07-larv-hc"><div className="fad07-num">38</div><div className="fad07-name">Dep. Trat.</div></div>
                <div className="fad07-larv-rowlbl">L1</div>
                <div className="fad07-larv-cell"><CelValor valor={dados?.campo_36_l1_tipo} /></div>
                <div className="fad07-larv-cell"><CelValor valor={dados?.campo_37_l1_qtde_g != null ? Number(dados.campo_37_l1_qtde_g).toFixed(1) : null} /></div>
                <div className="fad07-larv-cell"><CelValor valor={dados?.campo_38_l1_dep_trat} /></div>
              </div>
            </div>
          </div>

          {/* Linha 39..44 — Adulticida + totais + atenção */}
          <div className="fad07-flex-row">
            <div className="fad07-flex-box" style={{flex:4}}>
              <div className="fad07-larv cols-adult">
                <div className="fad07-larv-title">Adulticida</div>
                <div className="fad07-larv-hc"><div className="fad07-num">39</div><div className="fad07-name">Tipo</div></div>
                <div className="fad07-larv-hc"><div className="fad07-num">40</div><div className="fad07-name">Qtde. (cargas)</div></div>
                <div className="fad07-larv-cell">&nbsp;</div>
                <div className="fad07-larv-cell">&nbsp;</div>
              </div>
            </div>
            <div className="fad07-cell" style={{flex:2}}>
              <div className="fad07-h"><div className="fad07-num">43</div><div className="fad07-name tight">Total Agentes<br/>na Semana</div></div>
              <div className="fad07-v"><InputNum chave="campo_43_total_agentes" /></div>
            </div>
            <div className="fad07-cell" style={{flex:2}}>
              <div className="fad07-h"><div className="fad07-num">44</div><div className="fad07-name tight">Total Dias<br/>Trabalhados</div></div>
              <div className="fad07-v"><InputNum chave="campo_44_total_dias" /></div>
            </div>
            <div className="fad07-attention" style={{flex:4}}>
              <div className="fad07-att-title">Atenção!</div>
              <div><strong>37</strong> — Qtde. larvicida em gramas</div>
              <div><strong>43</strong> — Total de Agentes na semana</div>
              <div><strong>44</strong> — Total de Dias trabalhados</div>
            </div>
          </div>

          {/* ════ BLOCO 3 — RESUMO DO LABORATÓRIO (45-62) ════ */}
          <div className="fad07-banner gap-top">Resumo do Laboratório</div>

          {/* Matriz 45-52 — Depósitos com espécimes */}
          <div className="fad07-lab-mat fad07-lab-mat-a">
            <div className="fad07-lab-title">Nº depósitos com espécimes por tipo de depósito</div>
            <div className="fad07-lab-corner" />
            {(['A1','A2','B','C','D1','D2','E','Total'] as const).map((lbl, i) => (
              <div key={lbl} className="fad07-lab-colhdr">
                <div className="fad07-num">{45+i}</div>
                <div className="fad07-name">{lbl}</div>
              </div>
            ))}
            <div className="fad07-lab-rowlbl">com <em>&nbsp;Ae. aegypti</em></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_45_aeg_a1" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_46_aeg_a2" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_47_aeg_b" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_48_aeg_c" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_49_aeg_d1" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_50_aeg_d2" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_51_aeg_e" /></div>
            <div className="fad07-lab-cell"><InputNum chave="campo_52_aeg_total" /></div>
            <div className="fad07-lab-rowlbl">com <em>&nbsp;Ae. albopictus</em></div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell">&nbsp;</div>
            <div className="fad07-lab-cell"><InputNum chave="campo_52_alb_total" /></div>
          </div>

          {/* Matriz imóveis 53-58 + Exemplares 59-62 */}
          <div className="fad07-flex-row">
            <div className="fad07-flex-box" style={{flex:5}}>
              <div className="fad07-lab-mat fad07-lab-mat-imv">
                <div className="fad07-lab-title-spanning">Nº de imóveis com espécie, por tipo de imóvel</div>
                <div className="fad07-lab-corner" />
                {(['Residência','Comércio','T.Baldio','P.Estrat.','Outros','Total'] as const).map((lbl, i) => (
                  <div key={lbl} className="fad07-lab-colhdr">
                    <div className="fad07-num">{53+i}</div>
                    <div className="fad07-name">{lbl}</div>
                  </div>
                ))}
                {([
                  ['Ae. aegypti',   ['campo_53_aeg_residencia','campo_54_aeg_comercio','campo_55_aeg_tb','campo_56_aeg_pe','campo_57_aeg_outros','campo_58_aeg_total']],
                  ['Ae. albopictus',['campo_53_alb_residencia','campo_54_alb_comercio','campo_55_alb_tb','campo_56_alb_pe','campo_57_alb_outros','campo_58_alb_total']],
                  ['Outros',        ['campo_53_out_residencia','campo_54_out_comercio','campo_55_out_tb','campo_56_out_pe','campo_57_out_outros','campo_58_out_total']],
                ] as const).map(([lbl, keys]) => (
                  <React.Fragment key={String(lbl)}>
                    <div className="fad07-lab-rowlbl"><em>{lbl}</em></div>
                    {keys.map((k) => <div key={k} className="fad07-lab-cell"><InputNum chave={k} /></div>)}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="fad07-flex-box" style={{flex:3}}>
              <div className="fad07-lab-mat fad07-lab-mat-exm">
                <div className="fad07-lab-title">Nº de exemplares</div>
                {(['Larvas','Pupas','Exúvias','Adultos'] as const).map((lbl, i) => (
                  <div key={lbl} className="fad07-lab-colhdr">
                    <div className="fad07-num">{59+i}</div>
                    <div className="fad07-name">{lbl}</div>
                  </div>
                ))}
                {([
                  ['campo_59_aeg_larvas','campo_60_aeg_pupas','campo_61_aeg_exuvias','campo_62_aeg_adultos'],
                  ['campo_59_alb_larvas','campo_60_alb_pupas','campo_61_alb_exuvias','campo_62_alb_adultos'],
                  ['campo_59_out_larvas','campo_60_out_pupas','campo_61_out_exuvias','campo_62_out_adultos'],
                ] as const).map((keys, ri) => (
                  <React.Fragment key={ri}>
                    {keys.map((k) => <div key={k} className="fad07-lab-cell"><InputNum chave={k} /></div>)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* ════ BLOCO 4 — QUARTEIRÕES POSITIVOS (63, 64, 65) ════ */}
          <div className="fad07-flex-row" style={{marginTop:'1.6mm'}}>
            {([
              ['63','campo_63_quart_aegypti','com Ae. aegypti'],
              ['64','campo_64_quart_albopictus','com Ae. albopictus'],
              ['65','campo_65_quart_ambos','com ambas as espécies'],
            ] as const).map(([num, chave, desc]) => (
              <div key={num} className="fad07-quart">
                <div className="fad07-quart-title">
                  <div className="fad07-num">{num}</div>
                  <div className="fad07-name">Nº e seq. quarteirões<br/><em>{desc}</em></div>
                </div>
                <QuarteiraoGrid chave={chave} />
              </div>
            ))}
          </div>

          {/* ════ BLOCO 5 — RODAPÉ (66, 67) ════ */}
          <div className="fad07-row fad07-footer-row">
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">66</div><div className="fad07-name left">Visto do Supervisor</div></div>
              <div className="fad07-v signature">&nbsp;</div>
            </div>
            <div className="fad07-cell">
              <div className="fad07-h"><div className="fad07-num">67</div><div className="fad07-name left">Data do visto</div></div>
              <div className="fad07-v" style={{minHeight:'10mm', display:'flex', alignItems:'center', justifyContent:'center', gap:'1mm'}}>
                <span className="fad07-val-filled">{String(new Date().getDate()).padStart(2,'0')}</span>
                <span style={{color:'#666'}}>/</span>
                <span className="fad07-val-filled">{String(new Date().getMonth()+1).padStart(2,'0')}</span>
                <span style={{color:'#666'}}>/</span>
                <span className="fad07-val-filled">{new Date().getFullYear()}</span>
              </div>
            </div>
          </div>
          <div className="fad07-footer-mark" style={{marginTop:'0.6mm',fontSize:'6.5pt',fontWeight:700,letterSpacing:'0.04em'}}>FAD07 — Semanal</div>
          <div className="fad07-tovis-footer">Gerado pela plataforma TOVIS — Sistema de Gestão de Endemias — Walber Reis</div>

        </Box>
      </Box>

      <Snackbar
        open={!!snackMsg}
        autoHideDuration={4000}
        onClose={() => setSnackMsg(null)}
        message={snackMsg}
      />
    </Box>
  );
}
